import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { CalendarPlus } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { PlanWeekGrid, WeekPicker } from "@/components/PlanWeekGrid";
import { PLAN_DAYS, PLAN_MEALS, mondayOf } from "@/lib/mealPlan";

const label = (list, k) => list.find((x) => x.key === k)?.label;

export function usePlanWeek(open, initialWeek, planId) {
  const [weekStart, setWeekStart] = useState(() => initialWeek || mondayOf(new Date()));
  const [plan, setPlan] = useState(null);
  useEffect(() => {
    if (!open) return;
    setPlan(null);
    api.get("/meal-plan", { params: { week_start: weekStart, ...(planId ? { plan_id: planId } : {}) } }).then((r) => setPlan(r.data)).catch(() => setPlan({ slots: [] }));
  }, [open, weekStart, planId]);
  const slotAt = (day, meal) => (plan?.slots || []).find((s) => s.day === day && s.meal_type === meal);
  return { weekStart, setWeekStart, plan, slotAt, planId: planId || undefined };
}

export function ShopToggle({ plan, shop, setShop }) {
  if (plan?.synced_to_shopping) return <p className="text-xs text-rf-ink-soft" data-testid="plan-shop-synced">This week already feeds your shopping list, so it will update automatically.</p>;
  return (
    <label className="flex items-center gap-2 text-sm text-rf-ink cursor-pointer">
      <input type="checkbox" checked={shop} onChange={(e) => setShop(e.target.checked)} data-testid="plan-shop-toggle" className="accent-rf-orange w-4 h-4" />
      Add this week&rsquo;s ingredients to my shopping list too
    </label>
  );
}

export function planToast(nav, weekStart, msg) {
  toast.success(msg, { action: { label: "View plan", onClick: () => nav(`/planner?week=${weekStart}`) } });
}

export default function AddToPlanButton({ recipe }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { weekStart, setWeekStart, plan, slotAt } = usePlanWeek(open);
  const [pick, setPick] = useState(null);
  const [servings, setServings] = useState(parseInt(recipe.serves, 10) || 4);
  const [shop, setShop] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPick(null); }, [weekStart, open]);

  const taken = pick && slotAt(pick.day, pick.meal);
  const cell = (day, meal) => {
    const s = slotAt(day, meal);
    const picked = pick?.day === day && pick?.meal === meal;
    if (picked) return { text: s ? `Replace ${s.recipe_title}` : recipe.title, state: s ? "replace" : "new" };
    return s ? { text: s.recipe_title, state: "taken" } : { text: "", state: "empty" };
  };
  const add = async () => {
    setBusy(true);
    try {
      await api.put("/meal-plan/slot", { week_start: weekStart, day: pick.day, meal_type: pick.meal, recipe_id: recipe.recipe_id, servings });
      if (shop && !plan?.synced_to_shopping) await api.post("/meal-plan/add-to-shopping-list", { week_start: weekStart });
      planToast(nav, weekStart, `Added to ${label(PLAN_DAYS, pick.day)} ${label(PLAN_MEALS, pick.meal)}`);
      setOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Couldn't add to your plan"); }
    setBusy(false);
  };

  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid="add-to-plan-btn" className="btn-outline-ink !py-2.5 inline-flex items-center gap-2">
        <CalendarPlus size={18} /> Add to meal plan
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg w-[calc(100%-1.5rem)] rounded-2xl max-h-[92vh] overflow-y-auto" data-testid="add-to-plan-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Add to meal plan</DialogTitle>
            <DialogDescription>Tap a day and meal for <b>{recipe.title}</b>.</DialogDescription>
          </DialogHeader>
          <WeekPicker weekStart={weekStart} onChange={setWeekStart} />
          {plan ? <PlanWeekGrid weekStart={weekStart} cell={cell} onPick={(day, meal) => setPick({ day, meal })} />
            : <p className="text-sm text-rf-ink-soft py-8 text-center">Loading your week…</p>}
          <div className="flex items-center gap-3">
            <label htmlFor="plan-servings" className="text-sm font-semibold text-rf-ink">Servings</label>
            <input id="plan-servings" type="number" min={1} max={99} value={servings} data-testid="plan-servings-input"
                   onChange={(e) => setServings(Math.max(1, Math.min(99, parseInt(e.target.value, 10) || 1)))}
                   className="w-20 rounded-xl border border-rf-ink/15 px-3 py-2 text-sm" />
          </div>
          <ShopToggle plan={plan} shop={shop} setShop={setShop} />
          <button type="button" disabled={!pick || busy} onClick={add} data-testid="add-to-plan-confirm"
                  className={`w-full px-5 py-3 rounded-full font-bold text-white disabled:opacity-50 transition-colors ${taken ? "bg-red-600 hover:bg-red-700" : "bg-rf-orange hover:bg-rf-orange/90"}`}>
            {busy ? "Adding…" : !pick ? "Pick a day and meal" : taken ? `Replace ${taken.recipe_title}` : `Add to ${label(PLAN_DAYS, pick.day)} ${label(PLAN_MEALS, pick.meal)}`}
          </button>
        </DialogContent>
      </Dialog>
    </>
  );
}
